<h1>Bank Reconciliation</h1>
<nav mat-tab-nav-bar [tabPanel]="tabPanel">
  <a mat-tab-link *ngFor="let tab of tabs | visibleFinanceTabs" (click)="gotoTab(tab)" [active]="tab.link === activeTab.link">{{tab.title}}</a>
</nav>
<mat-tab-nav-panel #tabPanel>
  <mat-progress-bar *ngIf="loading || loadingRows || importing || exportingImportId || deletingImportId" mode="indeterminate"></mat-progress-bar>

  <ng-container *ngIf="!selectedImport; else importDetails">
    <section class="upload-panel">
      <h2>Import Bangor Savings CSV or TSV</h2>
      <p>Choose a Bangor Savings export, review its column mapping, then import it for deterministic reconciliation.</p>

      <div class="step">
        <h3><span>1</span> Select file</h3>
        <input #fileInput type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values" hidden (change)="selectFile($event)">
        <button mat-raised-button color="primary" (click)="fileInput.click()">Choose CSV or TSV</button>
        <span class="file-name" *ngIf="file">{{file.name}} ({{file.size / 1024 | number:'1.0-1'}} KB)</span>
        <span class="hint">One .csv or .tsv file, up to 5 MB.</span>
      </div>

      <div class="step" *ngIf="file">
        <h3><span>2</span> Map columns</h3>
        <div class="mapping-grid">
          <mat-form-field appearance="outline" *ngFor="let field of mappingFields">
            <mat-label>{{field.label}}{{field.required ? ' *' : ''}}</mat-label>
            <mat-select [(ngModel)]="mapping[field.key]">
              <mat-option [value]="undefined">Not mapped</mat-option>
              <mat-option *ngFor="let header of headers" [value]="header">{{header}}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <p class="hint">Map either Signed amount, or both Debit and Credit. Unmapped source columns are preserved by the backend.</p>
      </div>

      <div class="step" *ngIf="file">
        <h3><span>3</span> Import</h3>
        <div class="import-fields">
          <mat-form-field appearance="outline">
            <mat-label>Bank</mat-label>
            <input matInput value="Bangor Savings" disabled>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Account label</mat-label>
            <input matInput [(ngModel)]="accountLabel" maxlength="120">
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Account currency</mat-label>
            <input matInput [(ngModel)]="accountCurrency" maxlength="3" required>
          </mat-form-field>
        </div>
        <button mat-raised-button color="primary" [disabled]="importing || !isMappingValid()" (click)="importFile()">Import and reconcile</button>
        <span class="hint validation-hint" *ngIf="!isMappingValid()">{{mappingValidationMessage()}}</span>
      </div>
    </section>

    <section class="imports">
      <h2>Prior imports</h2>
      <div class="empty" *ngIf="!loading && !imports.length">
        <mat-icon>account_balance</mat-icon>
        <h3>No bank imports yet</h3>
        <p>Upload a Bangor Savings CSV or TSV above to compare its rows with Stripe, Dwolla, and Wallit ledger activity.</p>
      </div>
      <div class="table-scroll" *ngIf="imports.length">
        <table class="data-table">
          <thead><tr>
            <th>Created</th><th>Bank / account</th><th>Filename</th><th>Status</th><th>Total</th>
            <th>Matched</th><th>Partial</th><th>Unmatched</th><th>Ambiguous</th><th>Failed</th><th>Created by</th><th class="action-column">Actions</th>
          </tr></thead>
          <tbody><tr *ngFor="let item of imports" (click)="openImport(item)" tabindex="0" (keyup.enter)="openImport(item)">
            <td>{{item.createdAt ? (item.createdAt | date:'medium') : '-'}}</td>
            <td>{{item.bankName}}<small>{{item.accountLabel || 'No account label'}}</small></td>
            <td>{{item.sourceFilename}}</td><td>{{item.status}}</td><td>{{item.totalRows}}</td>
            <td>{{item.matchedRows}}</td><td>{{item.partiallyMatchedRows}}</td><td>{{item.unmatchedRows}}</td>
            <td>{{item.ambiguousRows}}</td><td>{{item.failedRows}}</td><td>{{formatCreatedBy(item.createdBy)}}</td>
            <td class="action-column" (click)="$event.stopPropagation()" (keyup.enter)="$event.stopPropagation()">
              <button
                mat-icon-button
                type="button"
                aria-label="Import actions"
                [disabled]="!!exportingImportId || !!deletingImportId"
                [matMenuTriggerFor]="priorImportActions">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #priorImportActions="matMenu">
                <button mat-menu-item (click)="exportImport(item, 'csv')"><mat-icon>download</mat-icon><span>Export CSV</span></button>
                <button mat-menu-item (click)="exportImport(item, 'tsv')"><mat-icon>download</mat-icon><span>Export TSV</span></button>
                <button mat-menu-item (click)="removeImport(item)"><mat-icon>delete</mat-icon><span>Remove import</span></button>
              </mat-menu>
            </td>
          </tr></tbody>
        </table>
      </div>
      <div class="pager" *ngIf="imports.length || importOffset">
        <button mat-button [disabled]="!importOffset" (click)="previousImports()">Previous</button>
        <span>Rows {{importOffset + 1}}–{{importOffset + imports.length}}</span>
        <button mat-button [disabled]="imports.length < pageSize" (click)="nextImports()">Next</button>
      </div>
    </section>
  </ng-container>

  <ng-template #importDetails>
    <button mat-button (click)="backToImports()"><mat-icon>arrow_back</mat-icon> Imports</button>
    <div class="detail-heading">
      <div><h2>{{selectedImport?.sourceFilename}}</h2><p>{{selectedImport?.bankName}} · {{selectedImport?.accountLabel || 'No account label'}}</p></div>
      <div class="detail-actions">
        <span class="status">{{selectedImport?.status}}</span>
        <button
          mat-icon-button
          type="button"
          aria-label="Import actions"
          [disabled]="!!exportingImportId || !!deletingImportId"
          [matMenuTriggerFor]="detailImportActions">
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #detailImportActions="matMenu">
          <button mat-menu-item (click)="exportImport(selectedImport!, 'csv', true)"><mat-icon>download</mat-icon><span>Export current results as CSV</span></button>
          <button mat-menu-item (click)="exportImport(selectedImport!, 'tsv', true)"><mat-icon>download</mat-icon><span>Export current results as TSV</span></button>
          <button mat-menu-item (click)="removeImport(selectedImport!)"><mat-icon>delete</mat-icon><span>Remove import</span></button>
        </mat-menu>
      </div>
    </div>
    <div class="summary-grid">
      <div><strong>{{selectedImport?.totalRows}}</strong><span>Total</span></div>
      <div class="matched"><strong>{{selectedImport?.matchedRows}}</strong><span>Matched</span></div>
      <div class="partial"><strong>{{selectedImport?.partiallyMatchedRows}}</strong><span>Partial</span></div>
      <div class="unmatched"><strong>{{selectedImport?.unmatchedRows}}</strong><span>Unmatched</span></div>
      <div class="ambiguous"><strong>{{selectedImport?.ambiguousRows}}</strong><span>Ambiguous</span></div>
      <div class="failed"><strong>{{selectedImport?.failedRows}}</strong><span>Failed</span></div>
    </div>
    <div class="row-toolbar">
      <mat-form-field appearance="outline" class="row-search">
        <mat-label>Search all fields</mat-label>
        <input matInput type="search" [ngModel]="rowSearch" (ngModelChange)="changeSearch($event)">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>State</mat-label>
        <mat-select [(ngModel)]="stateFilter" (selectionChange)="changeState()">
          <mat-option *ngFor="let state of states" [value]="state.value">{{state.label}}</mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-button (click)="loadRows(true)"><mat-icon>refresh</mat-icon> Refresh</button>
    </div>
    <div class="empty" *ngIf="!loadingRows && !rows.length">No reconciliation rows match the current search and state filter.</div>
    <div
      class="table-scroll results-scroll"
      *ngIf="rows.length"
      infiniteScroll
      [infiniteScrollDistance]="2"
      [infiniteScrollThrottle]="100"
      [scrollWindow]="false"
      (scrolled)="loadMoreRows()">
      <table class="data-table rows-table">
        <thead><tr>
          <th [attr.aria-sort]="sortAria('transactionDate')"><button type="button" class="sort-header" (click)="sortRows('transactionDate')">Date<mat-icon>{{sortIcon('transactionDate')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('description')"><button type="button" class="sort-header" (click)="sortRows('description')">Description<mat-icon>{{sortIcon('description')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('bankReference')"><button type="button" class="sort-header" (click)="sortRows('bankReference')">Bank reference<mat-icon>{{sortIcon('bankReference')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('direction')"><button type="button" class="sort-header" (click)="sortRows('direction')">Direction<mat-icon>{{sortIcon('direction')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('amount')"><button type="button" class="sort-header" (click)="sortRows('amount')">Bank amount<mat-icon>{{sortIcon('amount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('state')"><button type="button" class="sort-header" (click)="sortRows('state')">State<mat-icon>{{sortIcon('state')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('stateReason')"><button type="button" class="sort-header" (click)="sortRows('stateReason')">State reason<mat-icon>{{sortIcon('stateReason')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementProvider')"><button type="button" class="sort-header" (click)="sortRows('movementProvider')">Provider<mat-icon>{{sortIcon('movementProvider')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementExternalId')"><button type="button" class="sort-header" (click)="sortRows('movementExternalId')">Provider transaction / reference<mat-icon>{{sortIcon('movementExternalId')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementTransactionAmount')"><button type="button" class="sort-header" (click)="sortRows('movementTransactionAmount')">Transaction amount<mat-icon>{{sortIcon('movementTransactionAmount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementWallitFeeAmount')"><button type="button" class="sort-header" (click)="sortRows('movementWallitFeeAmount')">Wallit fee<mat-icon>{{sortIcon('movementWallitFeeAmount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementFeeAmount')"><button type="button" class="sort-header" (click)="sortRows('movementFeeAmount')">Provider fee<mat-icon>{{sortIcon('movementFeeAmount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementNetAmount')"><button type="button" class="sort-header" (click)="sortRows('movementNetAmount')">Net<mat-icon>{{sortIcon('movementNetAmount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('discrepancyAmount')"><button type="button" class="sort-header" (click)="sortRows('discrepancyAmount')">Discrepancy<mat-icon>{{sortIcon('discrepancyAmount')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('movementContractId')"><button type="button" class="sort-header" (click)="sortRows('movementContractId')">Contract<mat-icon>{{sortIcon('movementContractId')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('dimensions')"><button type="button" class="sort-header" (click)="sortRows('dimensions')">Dimensions<mat-icon>{{sortIcon('dimensions')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('matchedMovementId')"><button type="button" class="sort-header" (click)="sortRows('matchedMovementId')">Wallit movement ID<mat-icon>{{sortIcon('matchedMovementId')}}</mat-icon></button></th>
          <th [attr.aria-sort]="sortAria('ledgerTransactionId')"><button type="button" class="sort-header" (click)="sortRows('ledgerTransactionId')">Journal transaction ID<mat-icon>{{sortIcon('ledgerTransactionId')}}</mat-icon></button></th>
        </tr></thead>
        <tbody><tr *ngFor="let row of rows">
          <td>{{row.transactionDate || '-'}}</td><td>{{row.description || '-'}}</td>
          <td>{{row.bankReference || '-'}}</td><td>{{row.direction || '-'}}</td>
          <td>{{row.amount === null ? '-' : (row.amount | number:'1.2-2')}}</td>
          <td><span class="state-pill" [ngClass]="row.state.toLowerCase()">{{row.state}}</span></td>
          <td class="reason">{{row.stateReason}}</td><td>{{row.movementProvider || '-'}}</td><td>{{row.movementExternalId || '-'}}<small *ngIf="batchDetails(row)">{{batchDetails(row)}}</small></td>
          <td>{{row.movementTransactionAmount == null ? '-' : (row.movementTransactionAmount | number:'1.2-2')}}</td>
          <td>{{row.movementWallitFeeAmount == null ? '-' : (row.movementWallitFeeAmount | number:'1.2-2')}}</td>
          <td>{{row.movementFeeAmount == null ? '-' : (row.movementFeeAmount | number:'1.2-2')}}</td>
          <td>{{row.movementNetAmount == null ? '-' : (row.movementNetAmount | number:'1.2-2')}}</td>
          <td>{{row.discrepancyAmount == null ? '-' : (row.discrepancyAmount | number:'1.2-2')}}</td>
          <td>{{row.movementContractId || '-'}}</td><td>{{dimensions(row)}}</td><td>{{row.matchedMovementId || '-'}}</td>
          <td>{{row.ledgerTransactionId || '-'}}</td>
        </tr></tbody>
      </table>
    </div>
    <div class="infinite-status" *ngIf="rows.length">
      <mat-spinner *ngIf="loadingRows" diameter="24"></mat-spinner>
      <span *ngIf="loadingRows">Loading more results…</span>
      <span *ngIf="!loadingRows && hasMoreRows">Scroll for more results</span>
      <span *ngIf="!loadingRows && !hasMoreRows">All {{rows.length}} matching results loaded</span>
    </div>
  </ng-template>
</mat-tab-nav-panel>
